import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Upload, CheckCircle2, AlertCircle, Clock, Loader2, FileCheck, ArrowLeft, ExternalLink, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import DORAAnnotatedEditor from './DORAAnnotatedEditor';
import { DORAAnnotatedView } from '@/components/comparison/DORAAnnotatedView';
import { AnnotatedDocument } from '@/types/dora-annotations';
import api from '@/services/api';
import { useStore } from '@/store/useStore';
import { toast } from 'sonner';

interface DORADocument {
  document_id: number;
  filename: string;
  role: string;
  upload_order?: number;
}

interface DORAProjectInfo {
  id: number;
  project_id: number;
  client_name: string | null;
  status: string;
  template_document_id: number | null;
  filled_document_id: number | null;
  current_version: number;
  model_name?: string;
  generation_time_seconds?: number;
}

type WorkflowStep = 'upload' | 'analyze' | 'review' | 'complete';

interface EditorViewData {
  documentId: number;
  changes: any[];
  phase2Data?: {
    status: string;
    differences: Array<{
      topic: string;
      severity: 'CRITICAL' | 'MEDIUM' | 'LOW';
      buyer_text: string;
      seller_text: string;
      differences: string[];
      commercial_impact: string;
      recommendation: string;
      section_reference: string;
      buyer_paragraph_index?: number;
      buyer_paragraph_end?: number;
    }>;
    summary: {
      total_issues: number;
      critical_issues: number;
      medium_issues: number;
      low_issues: number;
    };
  };
  modelName?: string;
  generationTimeSeconds?: number;
  phase2Error?: string;
}

interface RepositoryDocument {
  id: number;
  filename: string;
  categories: string[];
  framework_relevance: Record<string, { score: number; reason: string }>;
  uploaded_at: string;
}

export default function DORAProjectView() {
  const { projectId } = useParams<{ projectId: string }>();
  const { selectedModel, setGeneratedDocuments, setSelectedDocIndex, setCurrentProjectId, setGeneratedDocument } = useStore();
  const [currentStep, setCurrentStep] = useState<WorkflowStep>('upload');
  const [doraInfo, setDoraInfo] = useState<DORAProjectInfo | null>(null);
  const [documents, setDocuments] = useState<DORADocument[]>([]);
  const [repositoryDocs, setRepositoryDocs] = useState<RepositoryDocument[]>([]);
  const [selectedRepositoryDocs, setSelectedRepositoryDocs] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisPhase, setAnalysisPhase] = useState<'template' | 'extraction' | 'generating' | 'complete'>('template');
  const [startTime, setStartTime] = useState<number>(0);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [blankCount, setBlankCount] = useState<number>(0);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [editorViewData, setEditorViewData] = useState<EditorViewData | null>(null);
  const [useFullComparison, setUseFullComparison] = useState<boolean>(true); // Default ON for DORA compliance
  const [annotatedDoc, setAnnotatedDoc] = useState<AnnotatedDocument | null>(null);
  const [runningSemanticComparison, setRunningSemanticComparison] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<{filename: string; content: string} | null>(null);

  useEffect(() => {
    if (projectId) {
      // Set current project ID in global store for sidebar
      setCurrentProjectId(projectId);
      initializeDORAProject();
      loadDocuments();
      loadRepositoryDocuments();
    }
  }, [projectId, setCurrentProjectId]);

  const loadRepositoryDocuments = async () => {
    try {
      console.log('🔍 Loading repository documents with framework=DORA');
      const response = await api.get('/repository/documents?framework=DORA');
      console.log('🔍 Repository API response:', response.data);
      console.log('🔍 Repository documents found:', response.data.documents?.length || 0);
      
      const docs = response.data.documents || [];
      
      // Sort by DORA relevance score (descending)
      const sortedDocs = docs.sort((a, b) => {
        const scoreA = a.framework_relevance?.DORA?.score || 0;
        const scoreB = b.framework_relevance?.DORA?.score || 0;
        return scoreB - scoreA;
      });
      
      setRepositoryDocs(sortedDocs);
      
      // Auto-select all documents by default
      setSelectedRepositoryDocs(new Set(sortedDocs.map(doc => doc.id)));
    } catch (error) {
      console.error('❌ Failed to load repository documents:', error);
    }
  };

  const initializeDORAProject = async () => {
    try {
      // Try to get existing DORA project info
      const response = await api.get(`/dora/projects/${projectId}/info`);
      setDoraInfo(response.data);
      
      // If document was previously generated, restore the diff view
      if (response.data.status === 'filled' && response.data.filled_document_id && response.data.template_document_id) {
        setCurrentStep('complete');
        
        // Load the comparison view automatically
        await loadExistingComparison(
          response.data.template_document_id,
          response.data.filled_document_id
        );
      } else {
        // Always start at upload if no filled document
        setCurrentStep('upload');
      }
    } catch (error: any) {
      // If not found, initialize new DORA project
      if (error.response?.status === 404) {
        try {
          const response = await api.post(`/dora/projects/${projectId}/init`);
          setDoraInfo(response.data);
          setCurrentStep('upload'); // New project always starts at upload
        } catch (initError) {
          console.error('Failed to initialize DORA project:', initError);
        }
      }
    }
  };

  const runSemanticComparison = async () => {
    if (!projectId) return;
    
    try {
      setRunningSemanticComparison(true);
      toast.info('Starting semantic comparison...');
      
      const response = await api.post(
        `/dora/projects/${projectId}/semantic-comparison?llm_provider=${selectedModel}`
      );
      
      if (response.data.status === 'warning') {
        toast.warning(response.data.message || 'No seller standard DORA found in repository');
      } else if (response.data.status === 'success') {
        setAnnotatedDoc(response.data);
        toast.success(`Found ${response.data.summary.total_semantic_issues} semantic issues`);
      }
    } catch (error: any) {
      console.error('Semantic comparison failed:', error);
      toast.error(error.response?.data?.detail || 'Semantic comparison failed');
    } finally {
      setRunningSemanticComparison(false);
    }
  };

  const handleGenerateFinalDocument = async (editedSections: Record<string, string>) => {
    toast.info('Generate final document feature coming soon');
    console.log('Edited sections:', editedSections);
  };

  const loadExistingComparison = async (templateDocId: number, generatedDocId: number) => {
    try {
      // Fetch the actual comparison data from backend
      const response = await api.get(`/dora/projects/${projectId}/comparison`);
      
      const editorData = {
        documentId: response.data.document_id || templateDocId,
        changes: response.data.changes || [],
        phase2Data: response.data.phase2_data || undefined,
        modelName: response.data.model_name || undefined,
        generationTimeSeconds: response.data.generation_time_seconds || undefined
      };
      
      setEditorViewData(editorData);
      
      // Set generated document in header for download button
      setGeneratedDocument({
        docId: editorData.documentId,
        filename: response.data.filename || 'dora_filled.docx'
      });
      
      console.log('Loaded previous comparison with changes');
    } catch (error) {
      console.error('Failed to load comparison:', error);
      // Fallback to basic view without changes
      setEditorViewData({
        documentId: templateDocId,
        changes: []
      });
      console.log('Loaded documents - comparison unavailable');
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await api.get(`/dora/projects/${projectId}/documents`);
      setDocuments(response.data);
      
      // Populate sidebar with analyzed documents (filled/generated)
      const analyzedDocs = response.data.filter((d: any) => d.role === 'filled' || d.role === 'generated');
      
      // Deduplicate by document_id to prevent duplicate entries in sidebar
      const uniqueDocs = analyzedDocs.reduce((acc: any[], doc: any) => {
        if (!acc.find(d => d.document_id === doc.document_id)) {
          acc.push(doc);
        }
        return acc;
      }, []);
      
      if (uniqueDocs.length > 0 && projectId) {
        const formattedDocs = uniqueDocs.map((doc: any) => ({
          document_id: doc.document_id,
          filename: doc.filename,
          role: doc.role,
          onSelect: async () => {
            // Load this document's comparison data
            try {
              const compResponse = await api.get(`/dora/projects/${projectId}/comparison/${doc.document_id}`);
              setEditorViewData({
                documentId: doc.document_id,
                changes: compResponse.data.changes || [],
                phase2Data: compResponse.data.phase2_data || undefined,
                modelName: compResponse.data.model_name || undefined,
                generationTimeSeconds: compResponse.data.generation_time_seconds || undefined
              });
              setGeneratedDocument({
                docId: doc.document_id,
                filename: compResponse.data.filename || doc.filename
              });
            } catch (error) {
              console.error('Failed to load document:', error);
              toast.error('Failed to load document');
            }
          }
        }));
        setGeneratedDocuments(formattedDocs, projectId);
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  };


  const handleUploadTemplate = async (files: FileList) => {
    if (!files || files.length === 0) return;
    
    setLoading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('file', files[i]);
        
        await api.post(
          `/dora/projects/${projectId}/template`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
      }
      
      console.log(`Uploaded ${files.length} template(s) successfully`);
      await loadDocuments();
    } catch (error: any) {
      console.error('Failed to upload template:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAnalysis = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setAnalyzing(false);
      setCurrentStep('upload');
      console.log('Analysis cancelled');
    }
  };

  const handleAnalyzeAndFill = async () => {
    setAnalyzing(true);
    setCurrentStep('analyze');
    setAnalysisPhase('template');
    const startTimeValue = Date.now(); // Use local variable for accurate timing
    setStartTime(startTimeValue);
    
    // Create abort controller for cancellation
    const controller = new AbortController();
    setAbortController(controller);
    
    try {
      // Step 1: Analyze template
      const analyzeResponse = await api.post(`/dora/projects/${projectId}/analyze`, null, {
        params: { llm_provider: selectedModel || 'claude' },
        signal: controller.signal
      });
      
      const blanks = analyzeResponse.data.total_blanks;
      setBlankCount(blanks);
      
      // Move to extraction phase
      setAnalysisPhase('extraction');
      
      // Step 2: Find answers from selected repository documents
      const repository_doc_ids = Array.from(selectedRepositoryDocs);
      console.log(`📋 Using ${repository_doc_ids.length} selected repository documents:`, repository_doc_ids);
      
      const answersResponse = await api.post(`/dora/projects/${projectId}/find-answers`, {
        llm_provider: selectedModel || 'claude',
        use_full_comparison: useFullComparison,
        repository_doc_ids: repository_doc_ids  // NEW: Pass repository doc IDs
      }, {
        signal: controller.signal
      });
      
      setAnalysisResult(answersResponse.data);
      
      // Don't show "complete" yet - document generation (Phase I + II) still running
      setAnalysisPhase('generating');
      
      // Auto-generate document and prepare for comparison view (includes Phase II)
      await handleGenerateAndCompare();
      
      // NOW mark as complete after everything is done
      setAnalysisPhase('complete');
      const elapsed = Math.round((Date.now() - startTimeValue) / 1000); // Use local variable
      setElapsedTime(elapsed);
      
      // Ensure we transition to complete view smoothly
      setCurrentStep('complete');
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        // User cancelled - already handled
        return;
      }
      console.error('Analysis failed:', error);
      setCurrentStep('upload');
    } finally {
      setAnalyzing(false);
      setAbortController(null);
    }
  };

  const handleGenerateAndCompare = async () => {
    try {
      // Generate the filled document (now runs both Phase I and Phase II)
      // Pass selected repository docs so Phase II only uses what user selected
      const repository_doc_ids = Array.from(selectedRepositoryDocs);
      const generateResponse = await api.post(`/dora/projects/${projectId}/generate`, {
        repository_doc_ids: repository_doc_ids
      }, {
        params: { llm_provider: selectedModel || 'claude' }
      });
      
      const { 
        document_id,
        filename,
        phase1,
        phase2,
        phase2_error
      } = generateResponse.data;

      console.log('🔍 DORA Generate Response (Phase I + II):', {
        document_id,
        filename,
        phase1Changes: phase1?.changes?.length || 0,
        phase2Issues: phase2?.differences?.length || 0,
        phase2Error: phase2_error
      });

      // Go directly to editor view with combined data
      setEditorViewData({
        documentId: document_id,
        changes: phase1?.changes || [],
        phase2Data: phase2 || undefined,
        phase2Error: phase2_error || undefined
      });
      
      // Set generated document in header for download button
      setGeneratedDocument({
        docId: document_id,
        filename: filename || 'dora_filled.docx'
      });
      
      setCurrentStep('complete');
    } catch (error: any) {
      console.error('Failed to generate document:', error);
      toast.error('Failed to generate document: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    try {
      await api.delete(`/dora/projects/${projectId}/documents/${documentId}`);
      console.log('Document deleted');
      await loadDocuments();
      await initializeDORAProject();
    } catch (error: any) {
      console.error('Failed to delete document:', error);
    }
  };

  const handlePreviewRepositoryDoc = async (docId: number, filename: string) => {
    try {
      const fileExt = filename.toLowerCase().split('.').pop();
      
      if (fileExt === 'pdf') {
        // For PDFs, download and open in new tab
        const response = await api.get(`/repository/documents/${docId}/content`, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } else {
        // For DOCX and other files, show text preview in modal
        toast.info('Loading document preview...');
        // Repository documents don't have a text extraction endpoint yet
        // For now, trigger download
        const response = await api.get(`/repository/documents/${docId}/content`, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      }
    } catch (error) {
      console.error('Failed to preview document:', error);
      toast.error('Failed to open document preview');
    }
  };

  const handlePreviewTemplateDoc = async (documentId: number, filename: string) => {
    try {
      const fileExt = filename.toLowerCase().split('.').pop();
      
      if (fileExt === 'pdf') {
        // For PDFs, download and open in new tab
        const response = await api.get(`/documents/${documentId}/download`, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } else {
        // For DOCX and other files, show text preview in modal
        const response = await api.get(`/documents/${documentId}/content`);
        setPreviewDoc({
          filename: filename,
          content: response.data.content
        });
      }
    } catch (error) {
      console.error('Failed to preview document:', error);
      toast.error('Failed to open document preview');
    }
  };


  const templateDocuments = documents.filter(d => d.role === 'template');
  const canAnalyze = selectedRepositoryDocs.size > 0 && templateDocuments.length > 0;

  // Show annotated view if semantic comparison was run
  if (annotatedDoc) {
    return (
      <div className="h-screen">
        <DORAAnnotatedView
          annotatedDoc={annotatedDoc}
          onGenerateFinal={handleGenerateFinalDocument}
          onBack={() => {
            setAnnotatedDoc(null);
            setCurrentStep('complete');
          }}
        />
      </div>
    );
  }

  // Show editor view if document was generated
  if (editorViewData) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <DORAAnnotatedEditor
          projectId={parseInt(projectId!)}
          documentId={editorViewData.documentId}
          changes={editorViewData.changes}
          phase2Data={editorViewData.phase2Data}
          onBack={() => {
            setEditorViewData(null);
            setCurrentStep('upload');
            setAnalysisResult(null);
          }}
        />
      </div>
    );
  }

  // Debug logging
  console.log('🔍 DORA View State:', {
    currentStep,
    loading,
    repositoryDocsCount: repositoryDocs.length,
    templateDocsCount: templateDocuments.length,
    editorViewData: !!editorViewData
  });

  return (
    <>
      {/* Document Preview Modal */}
      {previewDoc && (
        <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{previewDoc.filename}</DialogTitle>
              <DialogDescription>Document Preview (Text Extracted)</DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              <div className="bg-gray-50 dark:bg-gray-900 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-mono">
                  {previewDoc.content}
                </pre>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">DORA Contract Workflow</h1>
        <p className="text-muted-foreground">
          Analyze buyer DORA requirements and compare against seller standard terms
        </p>
      </div>

      {(currentStep === 'complete' || editorViewData) && (
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-3xl font-bold">Document Comparison</h1>
          <Button
            onClick={() => {
              setCurrentStep('upload');
              setEditorViewData(null);
              setGeneratedDocument(null); // Clear download button from header
              console.log('Workflow reset');
            }}
            variant="outline"
          >
            Reset Workflow
          </Button>
        </div>
      )}

      {currentStep === 'complete' && !editorViewData && (
        <Alert className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Loading previous comparison...
          </AlertDescription>
        </Alert>
      )}

      {/* Step Indicator */}
      <div className="flex items-center justify-between mb-8 relative">
        <div className="absolute top-4 left-0 right-0 h-0.5 bg-border -z-10" />
        
        <StepIndicator
          number={1}
          title="Upload Documents"
          active={currentStep === 'upload'}
          completed={currentStep !== 'upload'}
        />
        <StepIndicator
          number={2}
          title="Analyze & Fill"
          active={currentStep === 'analyze'}
          completed={currentStep === 'review' || currentStep === 'complete'}
        />
        <StepIndicator
          number={3}
          title="Review & Edit"
          active={currentStep === 'complete'}
          completed={false}
        />
      </div>

      {/* Upload Section */}
      {currentStep === 'upload' && (
        <div className="space-y-6">
          {/* Repository Documents - Info Only */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Repository Documents (Auto-Loaded)
              </CardTitle>
              <CardDescription>
                DORA-relevant documents from your repository will be used automatically as references
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {repositoryDocs.length === 0 ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No DORA documents in repository. Upload to <a href="/repository" className="underline font-medium">Repository</a> first.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-green-700 dark:text-green-400">
                      {selectedRepositoryDocs.size}/{repositoryDocs.length} document(s) selected
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (selectedRepositoryDocs.size === repositoryDocs.length) {
                          setSelectedRepositoryDocs(new Set());
                        } else {
                          setSelectedRepositoryDocs(new Set(repositoryDocs.map(d => d.id)));
                        }
                      }}
                    >
                      {selectedRepositoryDocs.size === repositoryDocs.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {repositoryDocs.map((doc) => {
                      const isSelected = selectedRepositoryDocs.has(doc.id);
                      return (
                        <div 
                          key={doc.id} 
                          className={`flex items-center justify-between p-3 border rounded-lg transition-colors ${
                            isSelected 
                              ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' 
                              : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 opacity-60'
                          }`}
                        >
                          <div 
                            className="flex items-center gap-3 flex-1 cursor-pointer"
                            onClick={() => {
                              const newSelected = new Set(selectedRepositoryDocs);
                              if (isSelected) {
                                newSelected.delete(doc.id);
                              } else {
                                newSelected.add(doc.id);
                              }
                              setSelectedRepositoryDocs(newSelected);
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}}
                              className="h-4 w-4 rounded border-gray-300"
                            />
                            <div>
                              <p className="font-medium text-sm">{doc.filename}</p>
                              <p className="text-xs text-muted-foreground">
                                Score: {doc.framework_relevance?.DORA?.score || 0}/10
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreviewRepositoryDoc(doc.id, doc.filename);
                              }}
                              title="Preview document"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                            <Badge variant="secondary" className={isSelected ? "bg-green-100 dark:bg-green-900" : ""}>
                              Repository
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>


          {/* Template Documents */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-orange-500" />
                Template Documents
              </CardTitle>
              <CardDescription>
                Upload one or more blank DORA contracts that need to be filled out
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border-2 border-dashed border-orange-200 rounded-lg p-8 text-center hover:border-orange-400 transition-colors">
                <input
                  type="file"
                  multiple
                  accept=".docx"
                  onChange={(e) => e.target.files && handleUploadTemplate(e.target.files)}
                  className="hidden"
                  id="template-upload"
                  disabled={loading}
                />
                <label htmlFor="template-upload" className="cursor-pointer">
                  <Upload className="h-10 w-10 mx-auto mb-4 text-orange-500" />
                  <div className="text-sm font-medium mb-1">
                    Drop template(s) here or click to browse
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Accepts: .docx only (multiple files allowed)
                  </div>
                </label>
              </div>

              {documents.filter(d => d.role === 'template').length > 0 && (
                <div className="space-y-2">
                  <span className="text-sm font-medium">Uploaded Templates ({documents.filter(d => d.role === 'template').length})</span>
                  {documents.filter(d => d.role === 'template').map((doc) => (
                    <div
                      key={doc.document_id}
                      className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-950 rounded-lg border border-orange-200"
                    >
                      <div className="flex items-center gap-3">
                        <Badge className="bg-orange-500">Template</Badge>
                        <span className="text-sm font-medium">{doc.filename}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePreviewTemplateDoc(doc.document_id, doc.filename)}
                          title="Preview document"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDocument(doc.document_id)}
                          disabled={loading}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Button */}
          <div className="flex flex-col items-center gap-4">
            <Button
              size="lg"
              onClick={handleAnalyzeAndFill}
              disabled={!canAnalyze || analyzing}
              className="min-w-[300px]"
            >
              {analyzing ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                  Analyzing...
                </>
              ) : (
                <>
                  Analyze & Fill Template
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
            
            {!canAnalyze && (
              <Alert className="max-w-md">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {documents.filter(d => d.role === 'template').length === 0 && 'Upload at least one template document'}
                  {selectedRepositoryDocs.size === 0 && documents.filter(d => d.role === 'template').length > 0 && 'Select at least one repository document'}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>
      )}

      {/* Analyzing Step */}
      {currentStep === 'analyze' && (
        <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950">
          <CardContent className="p-8">
            <div className="flex flex-col items-center gap-6">
              
              {/* Dynamic title */}
              <h3 className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                {analysisPhase === 'template' && '🤖 Analyzing Template...'}
                {analysisPhase === 'extraction' && '🔍 Extracting Answers...'}
                {analysisPhase === 'generating' && '📝 Running Compliance Analysis & Generating Document...'}
                {analysisPhase === 'complete' && '✅ Analysis Complete!'}
              </h3>
              
              {/* Dynamic status - single updating title */}
              <div className="text-center space-y-3 max-w-2xl">
                {analysisPhase === 'template' && (
                  <p className="text-xl font-medium text-blue-800 dark:text-blue-200">
                    Analyzing template document<span className="animate-pulse">...</span>
                  </p>
                )}
                
                {analysisPhase === 'extraction' && (
                  <p className="text-xl font-medium text-green-800 dark:text-green-200">
                    Extracting answers from {selectedRepositoryDocs.size} selected document(s)<span className="animate-pulse">...</span>
                  </p>
                )}
                
                {analysisPhase === 'generating' && (
                  <p className="text-xl font-medium text-purple-800 dark:text-purple-200">
                    Running compliance analysis and generating filled document<span className="animate-pulse">...</span>
                  </p>
                )}
                
                {analysisPhase === 'complete' && (
                  <>
                    <p className="text-lg font-bold text-green-700 dark:text-green-300">
                      ✨ Analysis Complete!
                    </p>
                    <div className="text-sm text-blue-700 dark:text-blue-300 mt-3 space-y-2">
                      <div className="flex items-center justify-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span>Answered {analysisResult?.answered || 0}/{analysisResult?.total_blanks || 0} blanks</span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <span>⏱️ Duration: {Math.floor(elapsedTime / 60)}m {elapsedTime % 60}s</span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <span>🤖 Model: {selectedModel === 'ollama' ? 'Ollama (Local)' : 'Claude (Cloud)'}</span>
                      </div>
                    </div>
                  </>
                )}
                
                {analysisPhase !== 'complete' && (
                  <Button 
                    onClick={handleCancelAnalysis}
                    variant="outline"
                    className="mt-4 border-red-500 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                  >
                    Cancel Analysis
                  </Button>
                )}
              </div>
              
              {/* Progress bar */}
              <Progress 
                value={analysisPhase === 'template' ? 25 : analysisPhase === 'extraction' ? 50 : analysisPhase === 'generating' ? 75 : 100} 
                className="w-full max-w-md h-2" 
              />
            </div>
          </CardContent>
        </Card>
      )}
      </div>
    </>
  );
}

interface StepIndicatorProps {
  number: number;
  title: string;
  active: boolean;
  completed: boolean;
}

function StepIndicator({ number, title, active, completed }: StepIndicatorProps) {
  return (
    <div className="flex flex-col items-center gap-2 bg-background px-4 relative z-10">
      <div
        className={`flex items-center justify-center w-10 h-10 rounded-full border-2 font-semibold transition-all ${
          completed
            ? 'bg-primary border-primary text-primary-foreground'
            : active
            ? 'bg-primary/10 border-primary text-primary'
            : 'bg-background border-border text-muted-foreground'
        }`}
      >
        {completed ? <CheckCircle2 className="h-5 w-5" /> : number}
      </div>
      <div
        className={`text-sm font-medium whitespace-nowrap ${
          active || completed ? 'text-foreground' : 'text-muted-foreground'
        }`}
      >
        {title}
      </div>
    </div>
  );
}
